import React, { useState, useEffect, useCallback, useRef } from "react";
import BrickwallGrid from "./Brickwall/BrickwallGrid";

const Brickwall = ({
	register,
	error,
	errors,
	required = false,
	id,
	field,
	watch,
	setValue,
	config,
	setCurrentStep,
	trigger,
}) => {
	// Get wall data from config.data (loaded via filter)
	const configWallData =
		config?.data?.[`${id}_wall_data`] || config?.data?.brickwall_wall_data;

	// Default initial wall data
	const defaultWallData = {
		rows: (() => {
			const highestRow = getHighestBrickYValue(configWallData);
			// If we have any bricks, add extra rows above the highest one.
			// Otherwise, fall back to a sensible default.
			return highestRow > 0 ? highestRow + 50 : 200;
		})(),
		bricksPerRow: 7,
		brickPrice: 50,
		donations: [],
	};

	// Use wall data from config if available, otherwise use default
	const initialWallData = configWallData || defaultWallData;

	// Get wall data from form
	const wallDataValue = watch(`${id}_wall_data`);
	const wallData = wallDataValue || initialWallData;

	// Brick coordinate type: [row, col]
	const getOccupiedBricks = useCallback(() => {
		const occupied = new Map();
		if (wallData.donations) {
			wallData.donations.forEach((donation) => {
				if (donation.bricks) {
					donation.bricks.forEach((brick) => {
						occupied.set(`${brick[0]},${brick[1]}`, donation);
					});
				}
			});
		}
		return occupied;
	}, [wallData]);

	const [selectedBricks, setSelectedBricks] = useState(new Set());
	const [isPainting, setIsPainting] = useState(false);
	const [paintMode, setPaintMode] = useState("add");
	const [hoveredBrick, setHoveredBrick] = useState(null);
	const [showConfetti, setShowConfetti] = useState(false);
	const containerRef = useRef(null);

	// Watch form values for brick display
	const donorName = watch("brick_donor_name") || "";
	const brickColor = watch("brick_colour") || "#00C34B";
	const donationAmount =
		watch("brick_amount") ||
		watch("donation_frequency_and_amount[amount]") ||
		50;
	const hideNameOnBrick = watch("brickwall_hide_name") == 1;
	const hideAmountOnBrick = watch("brickwall_hide_amount") == 1;

	// Initialize wall data if not present
	useEffect(() => {
		if (!wallDataValue && initialWallData) {
			setValue(`${id}_wall_data`, initialWallData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wallDataValue, setValue, id]);

	// Sync selected bricks with form
	useEffect(() => {
		setValue(`${id}_selected_bricks`, Array.from(selectedBricks));
	}, [selectedBricks, setValue, id]);

	// Check if a brick has a path to the ground (row 0)
	const hasPathToGround = useCallback(
		(row, col, visited = new Set(), testSelectedSet = null) => {
			const key = `${row},${col}`;
			const selectedSet =
				testSelectedSet !== null ? testSelectedSet : selectedBricks;

			if (row === 0) return true;
			if (visited.has(key)) return false;
			visited.add(key);

			const occupied = getOccupiedBricks();
			const brickExists = occupied.has(key) || selectedSet.has(key);
			if (!brickExists) return false;

			const isEvenRow = row % 2 === 0;
			let supportingBricks = [];

			if (isEvenRow) {
				supportingBricks = [
					[row - 1, col - 1],
					[row - 1, col],
				];
			} else {
				supportingBricks = [
					[row - 1, col],
					[row - 1, col + 1],
				];
			}

			return supportingBricks.some(([r, c]) => {
				const supportKey = `${r},${c}`;
				if (occupied.has(supportKey) || selectedSet.has(supportKey)) {
					return hasPathToGround(r, c, visited, testSelectedSet);
				}
				return false;
			});
		},
		[getOccupiedBricks, selectedBricks],
	);

	// Check if a brick can be placed (physics constraint)
	const canPlaceBrick = useCallback(
		(row, col) => {
			const occupied = getOccupiedBricks();

			if (occupied.has(`${row},${col}`)) return false;
			if (row === 0) return true;

			const isEvenRow = row % 2 === 0;
			let supportingBricks = [];

			if (isEvenRow) {
				supportingBricks = [
					[row - 1, col - 1],
					[row - 1, col],
				];
			} else {
				supportingBricks = [
					[row - 1, col],
					[row - 1, col + 1],
				];
			}

			return supportingBricks.some(([r, c]) => {
				const supportKey = `${r},${c}`;
				if (occupied.has(supportKey)) {
					// For availability, only consider permanently occupied bricks.
					// The current in-progress selection should not change which
					// bricks are structurally allowed, so we call hasPathToGround
					// with an empty testSelectedSet.
					return hasPathToGround(r, c, new Set(), new Set());
				}
				return false;
			});
		},
		[getOccupiedBricks, hasPathToGround],
	);

	// Check if two bricks are adjacent
	const areAdjacent = (row1, col1, row2, col2) => {
		if (Math.abs(row1 - row2) > 1) return false;

		if (row1 === row2) {
			return Math.abs(col1 - col2) === 1;
		} else {
			const rowDiff = row2 - row1;
			const isRow1Even = row1 % 2 === 0;

			if (rowDiff === 1) {
				if (isRow1Even) {
					return col2 === col1 || col2 === col1 + 1 || col2 === col1 - 1;
				} else {
					return col2 === col1 - 1 || col2 === col1 || col2 === col1 + 1;
				}
			} else {
				const isRow2Even = row2 % 2 === 0;
				if (isRow2Even) {
					return col1 === col2 || col1 === col2 + 1 || col1 === col2 - 1;
				} else {
					return col1 === col2 - 1 || col1 === col2 || col1 === col2 + 1;
				}
			}
		}
	};

	// Check if a brick is connected to the current selection
	const isConnectedToSelection = useCallback(
		(row, col) => {
			if (selectedBricks.size === 0) return true;

			return Array.from(selectedBricks).some((brick) => {
				const [r, c] = brick.split(",").map(Number);
				return areAdjacent(row, col, r, c);
			});
		},
		[selectedBricks],
	);

	// Check if a brick is available for selection
	const isBrickAvailable = useCallback(
		(row, col) => {
			const occupied = getOccupiedBricks();
			const brickKey = `${row},${col}`;

			// If occupied, not available
			if (occupied.has(brickKey)) return false;

			// Only rely on physics (canPlaceBrick). We now only allow a single brick
			// selection at a time, so adjacency rules are not required.
			return canPlaceBrick(row, col);
		},
		[getOccupiedBricks, canPlaceBrick],
	);

	// Find all bricks that depend on a given brick (bricks above that would lose support)
	const findDependentBricks = useCallback(
		(row, col, selectedSet) => {
			const dependent = new Set();
			const occupied = getOccupiedBricks();
			const removedBrickKey = `${row},${col}`;

			// Check all bricks in rows above the removed brick
			for (let checkRow = row + 1; checkRow < wallData.rows; checkRow++) {
				const isEvenRow = checkRow % 2 === 0;
				const maxCol = wallData.bricksPerRow - (isEvenRow ? 0 : 1);

				for (let checkCol = 0; checkCol < maxCol; checkCol++) {
					const checkKey = `${checkRow},${checkCol}`;
					if (!selectedSet.has(checkKey)) {
						continue;
					}

					// Check if this brick depends on the removed brick
					const supportingBricks = isEvenRow
						? [
								[checkRow - 1, checkCol - 1],
								[checkRow - 1, checkCol],
							]
						: [
								[checkRow - 1, checkCol],
								[checkRow - 1, checkCol + 1],
							];

					const dependsOnRemoved = supportingBricks.some(
						([sr, sc]) => sr === row && sc === col,
					);

					if (dependsOnRemoved) {
						// Check if this brick still has a path to ground without the removed brick
						const testSet = new Set(selectedSet);
						testSet.delete(removedBrickKey);
						if (!hasPathToGround(checkRow, checkCol, new Set(), testSet)) {
							dependent.add(checkKey);
						}
					}
				}
			}

			// Recursively find bricks that depend on the newly found dependent bricks
			const toCheck = Array.from(dependent);
			const checked = new Set(dependent);

			for (const depKey of toCheck) {
				const [depRow, depCol] = depKey.split(",").map(Number);
				for (let checkRow = depRow + 1; checkRow < wallData.rows; checkRow++) {
					const isEvenRow = checkRow % 2 === 0;
					const maxCol = wallData.bricksPerRow - (isEvenRow ? 0 : 1);

					for (let checkCol = 0; checkCol < maxCol; checkCol++) {
						const checkKey = `${checkRow},${checkCol}`;
						if (!selectedSet.has(checkKey) || checked.has(checkKey)) {
							continue;
						}

						const supportingBricks = isEvenRow
							? [
									[checkRow - 1, checkCol - 1],
									[checkRow - 1, checkCol],
								]
							: [
									[checkRow - 1, checkCol],
									[checkRow - 1, checkCol + 1],
								];

						const dependsOnDep = supportingBricks.some(
							([sr, sc]) => sr === depRow && sc === depCol,
						);

						if (dependsOnDep) {
							const testSet = new Set(selectedSet);
							testSet.delete(removedBrickKey);
							dependent.forEach((k) => testSet.delete(k));
							if (!hasPathToGround(checkRow, checkCol, new Set(), testSet)) {
								dependent.add(checkKey);
								checked.add(checkKey);
								toCheck.push(checkKey);
							}
						}
					}
				}
			}

			return dependent;
		},
		[getOccupiedBricks, wallData, hasPathToGround],
	);

	// Handle mouse events for painting
	const handleBrickMouseDown = (row, col, event) => {
		event.preventDefault();
		event.stopPropagation();

		const brickKey = `${row},${col}`;
		const isSelected = selectedBricks.has(brickKey);
		const occupied = getOccupiedBricks();
		const isOccupied = occupied.has(brickKey);

		// If occupied, don't allow selection/deselection
		if (isOccupied) return;

		// Check if brick is available for selection
		if (!isBrickAvailable(row, col)) return;

		setIsPainting(true);

		if (event.shiftKey || isSelected) {
			// Deselect mode
			setPaintMode("remove");
			const newSelected = new Set(selectedBricks);
			newSelected.delete(brickKey);

			// Find and unselect all dependent bricks
			const dependentBricks = findDependentBricks(row, col, selectedBricks);
			dependentBricks.forEach((key) => newSelected.delete(key));

			setSelectedBricks(newSelected);
		} else {
			// Select mode
			setPaintMode("add");
			// Only allow a single selected brick at any time
			const newSelected = new Set([brickKey]);
			setSelectedBricks(newSelected);
		}
	};

	const handleBrickMouseEnter = (row, col) => {
		setHoveredBrick(`${row},${col}`);

		if (isPainting && isBrickAvailable(row, col)) {
			const brickKey = `${row},${col}`;
			const occupied = getOccupiedBricks();
			const isOccupied = occupied.has(brickKey);

			// If occupied, don't allow selection/deselection
			if (isOccupied) return;

			if (paintMode === "add") {
				setSelectedBricks(() => {
					// Only allow a single selected brick while painting
					const newSet = new Set([brickKey]);
					return newSet;
				});
			} else if (paintMode === "remove") {
				setSelectedBricks((prev) => {
					const newSet = new Set(prev);
					newSet.delete(brickKey);

					// Find and unselect all dependent bricks
					const dependentBricks = findDependentBricks(row, col, prev);
					dependentBricks.forEach((key) => newSet.delete(key));

					return newSet;
				});
			}
		}
	};

	const handlePaintingMouseUp = () => {
		setIsPainting(false);
	};

	useEffect(() => {
		document.addEventListener("mouseup", handlePaintingMouseUp);
		return () => {
			document.removeEventListener("mouseup", handlePaintingMouseUp);
		};
	}, []);

	// Handle scroll effect: update background position based on scrollX
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const gridContainer = container.querySelector(".brickwall-grid-container");
		if (!gridContainer) return;

		const handleScroll = () => {
			const scrollX = gridContainer.scrollLeft;
			if (window.innerWidth < 800) {
				const baseX = -58;
				const newPositionX = baseX - scrollX;
				container.style.backgroundPositionX = `${newPositionX}px`;
			} else {
				container.style.removeProperty("background-position-x");
			}
		};

		const handleResize = () => {
			// Recalculate on resize to handle breakpoint changes
			handleScroll();
		};

		// Set scroll to midpoint on load
		const setScrollToMidpoint = () => {
			const scrollWidth = gridContainer.scrollWidth;
			const clientWidth = gridContainer.clientWidth;
			const maxScroll = scrollWidth - clientWidth;
			if (maxScroll > 0) {
				gridContainer.scrollLeft = maxScroll / 2;
			}
		};

		// Wait for layout to be complete before setting scroll position
		// Use requestAnimationFrame to ensure DOM is ready, then a small timeout
		// to ensure content is fully rendered
		requestAnimationFrame(() => {
			setTimeout(() => {
				setScrollToMidpoint();
				handleScroll();
			}, 0);
		});

		gridContainer.addEventListener("scroll", handleScroll);
		window.addEventListener("resize", handleResize);

		return () => {
			gridContainer.removeEventListener("scroll", handleScroll);
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const handleDonate = async () => {
		const fieldsToValidate = [
			`${id}_selected_bricks`,
			"brick_donor_name",
			"brick_amount",
			"donation_frequency_and_amount[amount]",
		];

		const isValid = trigger ? await trigger(fieldsToValidate) : true;
		if (!isValid) {
			return;
		}

		if (selectedBricks.size === 0) {
			alert("Please select a brick!");
			return;
		}

		setCurrentStep((prev) => prev + 1);
	};

	// Clear selection
	const clearSelection = () => {
		setSelectedBricks(new Set());
	};

	const occupied = getOccupiedBricks();

	return (
		<>
			<div className="brickwall-field-inner">
				{/* Hidden inputs for form integration */}
				<input
					type="hidden"
					{...register(`${id}_selected_bricks`, {
						required: required ? "Please select at least one brick" : false,
					})}
				/>
				<input type="hidden" {...register(`${id}_donor_name`)} />
				<input type="hidden" {...register(`${id}_wall_data`)} />

				<div className="brickwall-container" ref={containerRef}>
					<BrickwallGrid
						wallData={wallData}
						occupied={occupied}
						selectedBricks={selectedBricks}
						hoveredBrick={hoveredBrick}
						donationAmount={donationAmount}
						canPlaceBrick={canPlaceBrick}
						isConnectedToSelection={isConnectedToSelection}
						isBrickAvailable={isBrickAvailable}
						onBrickMouseDown={handleBrickMouseDown}
						onBrickMouseEnter={handleBrickMouseEnter}
						donorName={donorName}
						brickColor={brickColor}
						hideNameOnBrick={hideNameOnBrick}
						hideAmountOnBrick={hideAmountOnBrick}
						config={config}
						register={register}
						errors={errors}
						setValue={setValue}
						watch={watch}
						trigger={trigger}
						onDonate={handleDonate}
						onClearSelection={clearSelection}
					/>
				</div>

				{/* Confetti Animation */}
				{showConfetti && (
					<div className="brickwall-confetti">
						<div className="brickwall-confetti-emoji">🎉</div>
						<div className="brickwall-confetti-text">Thank You! ❤️</div>
					</div>
				)}

				{error && <div className="field__error">{error.message}</div>}
			</div>
		</>
	);
};

// Reusable helper to determine the highest brick row (y value) in the donations data
export const getHighestBrickYValue = (configWallData) => {
	if (
		!configWallData ||
		!Array.isArray(configWallData.donations) ||
		configWallData.donations.length === 0
	) {
		return 0;
	}

	let maxRow = 0;

	configWallData.donations.forEach((donation) => {
		if (donation.bricks && Array.isArray(donation.bricks)) {
			donation.bricks.forEach((brick) => {
				const row = Array.isArray(brick)
					? brick[0]
					: typeof brick === "string"
						? parseInt(brick.split(",")[0], 10)
						: 0;

				if (!isNaN(row) && row > maxRow) {
					maxRow = row;
				}
			});
		}
	});

	return maxRow;
};

export default Brickwall;
